/**
 * Workspace-shell analyzing singleton.
 *
 * Wraps @symbo.ls/analyzing so every sub-app + the shell itself share one
 * session, one user identity, and one transport. Boots once the SDK is
 * available so the envelope ships through sdk.execute('workspaceProject.analyzed',
 * 'ingest', …) — a deprecated SDK-level alias that delegates to the main API
 * server's Mongo-backed POST /core/analyzed/ingest route (the old
 * Supabase-backed workspace-project worker surface it used to name was
 * deleted with the Supabase plane, 2026-07-27).
 *
 * Replaces Grafana Faro. No dedicated worker, no separate endpoint.
 *
 * Usage from app.js:
 *   import { bootAnalyzing, identifyAnalyzing, reidentifyOnOrgSwitch } from './analyzing.js'
 *   onCreate: (el, s) => {
 *     bootAnalyzing({ sdk: el.sdk })
 *     identifyAnalyzing({ user: s.user })
 *   }
 *
 * Failures are swallowed — telemetry breaking the shell would be worse than
 * the missing data.
 *
 * ATTRIBUTION (tickets/opus.md "analyzed ingest: real per-app attribution"):
 * `appKey` is a caller-supplied parameter (see `bootAnalyzing` below), no
 * longer a hardcoded literal — server/src/domains/analyzed/services/
 * AnalyzedWriteService.js's writeEnvelope() already derives the stored
 * `projectId` from `envelope.project_id || app?.key`, authoritatively
 * scoped underneath a SEPARATE, server/JWT-resolved `workspace` (tenant)
 * field — so any caller of bootAnalyzing MAY stamp its own identity
 * (ideally its manifest's canonical `key`, e.g. 'system--canvas') without
 * opening any cross-tenant vector: a spoofed appKey can only mislabel data
 * that already belongs to the caller's own authenticated tenant.
 *
 * THIS FILE's own call site (workspace/packages/workspace/app.js) keeps
 * the literal 'workspace' default deliberately: app.js is the unified
 * my.symbols.app shell's OWN root component — it is never shared with
 * canvas/marketplace/preview's standalone app.js files (each ships its
 * own), so 'workspace' is the accurate identity here, not a placeholder.
 * The shell DOES lazy-route canvas/marketplace/preview/native pages
 * within this one session (workspace/packages/workspace/index.js's
 * `apps` map + the smbls default router over merged `context.pages`),
 * but that routing is deliberately NOT used to further split this
 * `appKey`/`projectId`, for two concrete reasons:
 *   1. Each sub-app's `manifest.routes` regex array is consulted ONLY by
 *      bootShell's single-app STANDALONE dispatch (`_singleAppDispatch`
 *      in shared/functions/bootShell.js) — the unified multi-app shell
 *      never matches URLs against it. Reusing it here anyway is actively
 *      wrong: canvas/manifest.js's own catch-all `/^\/[^/]+\/[^/]+/`
 *      matches the primary Scheme-A workspace URL shape
 *      (`/w/<org>/<workspace>/...`), so naive regex reuse would
 *      mislabel effectively all native workspace traffic as "canvas".
 *   2. Even a reliable per-route signal would face the same problem the
 *      ticket flags for native routes (/tasks vs /notes vs /excalidraw):
 *      one continuous browser session can legitimately cross from
 *      native pages into canvas into marketplace, and a single
 *      session-level `projectId` can't cleanly represent that — forcing
 *      a split is a product call about analytics granularity, not an
 *      engineering default. `route` already rides on every EVENT
 *      (AnalyzedWriteService: `eventDocs[].route`), so a rollup can
 *      still derive per-route/per-surface breakdowns later without the
 *      shell guessing at a taxonomy here.
 * Genuinely separate deployments (canvas/marketplace/preview/analytics's
 * OWN standalone `bootShell()` entry points, and any workspace/apps/*
 * platform feature app) are real, unambiguous, single-app sessions and
 * are the correct place to pass a distinct `appKey` — that wiring lives
 * outside this package (workspace/packages/canvas, workspace/packages/
 * shared, workspace/apps/*) and is out of this file's scope.
 */

import { createAnalyzing } from '@symbo.ls/analyzing'
import { currentChannel } from '@symbo.ls/channels'

let _instance = null
let _booting = false
// Resolves when the async tracker construction settles (see bootAnalyzing).
let _bootPromise = null
// Tenant scope — every envelope carries `workspace_id` so the server's
// /core/analyzed/ingest can route the write to the correct tenant
// bucket without relying on `req.user.activeOrganization`. The
// workspace shell stamps this from the unified-auth-boot snapshot at
// init (orgs[0] / Mongo activeOrg / gov JWT — same resolver the rest
// of the shell uses) and refreshes it on every `sdk.orgSwitched`
// event so subsequent telemetry lands in the new tenant. Stays
// module-level so the beforeSend closure can read the latest value
// without rebinding the analyzing instance.
let _workspaceId = null

/**
 * Update the tenant scope. Called from the shell's `sdk.orgSwitched`
 * handler and from any other surface that switches the active workspace.
 * Pass null/empty to clear (signed-out state, anonymous boot).
 */
export const setAnalyzingWorkspace = (id) => {
  const next = id ? String(id) : null
  // The boot set (null → id) of a FULL page load: the tracker may have
  // reused the tab's stored session id from a page in ANOTHER workspace
  // (ANALYZING-SESSION-BOOT-ROTATE-1). Rotate there, same path as below.
  if (_workspaceId == null && next != null && _rotateIfStoredScopeDiffers(next)) return
  const changed = _workspaceId != null && next != null && next !== _workspaceId
  // A CHANGE of tenant scope mid-page ends the current analyzing session and
  // starts a fresh one (tickets/analytics.md ANALYZING-SESSION-ROTATE-1). The
  // server keys a session row on {_id, workspace}: the same session id under
  // the new workspace is refused (409) for the rest of the session — every
  // post-switch beacon used to be dropped silently. Only a real change
  // rotates: the boot set (null → id), a repeat of the same id, and a clear
  // (id → null) do not. `_instance` may be null pre-boot; nothing to rotate.
  //
  // ORDER MATTERS: rotate BEFORE re-stamping `_workspaceId`. The rotation
  // flushes the old session's queue plus its terminal envelope synchronously
  // through beforeSend, which reads `_workspaceId` — so that last envelope is
  // stamped with the OLD workspace and the server (member-gated explicit
  // stamp on /ingest) files it under the old session regardless of when the
  // user's Mongo claim flips. Measured the other way round: 6× 409 on the
  // old id, endedAt never written, its last batch lost.
  if (changed && _instance && typeof _instance.startNewSession === 'function') {
    _instance.startNewSession()
    _workspaceId = next
    // Open the new session on the wire right away (the dashboard's Who's
    // Active reads sessions, not just events) instead of waiting for the
    // next organic event.
    try {
      _instance.captureMessage('session.start', 'info', {
        reason: 'workspace-switch',
        workspaceId: next
      })
    } catch (_) {}
    // Fresh session, fresh page-view dedupe: the path on screen after the
    // switch is the NEW session's first page view, not a consecutive repeat
    // of the old session's last one. The next navigation (org switches
    // almost always route) re-emits even when the pathname is identical.
    _lastPageViewPath = null
    return
  }
  _workspaceId = next
}

// ── Session scope across full page loads (ANALYZING-SESSION-BOOT-ROTATE-1) ──
// @symbo.ls/analyzing keeps the tab's session id in sessionStorage
// (`smbls_sid`) and REUSES it on the next page load inside its timeout —
// one visit is one session across reloads. The workspace a session belongs
// to is not in that record (the tracker knows no tenant), so a FULL-PAGE
// navigation from workspace A into workspace B (Fly "Make it real" →
// magic-callback → /w/<org>/<ws-2>) booted B with A's id, and the server
// (session row keyed {_id, workspace}) refused every envelope with 409
// "session id belongs to another workspace". The live-switch rotation in
// setAnalyzingWorkspace never fires there: the boot set is null → id.
//
// So the shell keeps its own per-tab record beside the tracker's:
// `smbls_sid_ws` = { sessionId, workspaceId } of the last envelope that
// actually shipped (written from beforeSend — the server row exists only
// once an envelope shipped). On the boot set, when the record names the
// CURRENT session id under a DIFFERENT workspace, the session rotates; a
// boot into the same workspace (a reload) keeps it. A record for another id
// means the tracker minted a fresh session — no row exists yet, nothing to
// rotate. Storage is read through globalThis per call (no module state);
// every access is guarded — telemetry never breaks the shell.
export const ANALYZING_SESSION_SCOPE_KEY = 'smbls_sid_ws'

const _sessionScopeStorage = () => {
  try {
    return typeof globalThis !== 'undefined' ? globalThis.sessionStorage || null : null
  } catch (_) {
    return null
  }
}

const _readSessionScope = () => {
  try {
    const raw = _sessionScopeStorage()?.getItem?.(ANALYZING_SESSION_SCOPE_KEY)
    if (!raw) return null
    const rec = JSON.parse(raw)
    if (!rec || typeof rec.sessionId !== 'string' || typeof rec.workspaceId !== 'string')
      return null
    if (!rec.sessionId || !rec.workspaceId) return null
    return rec
  } catch (_) {
    return null
  }
}

/**
 * Record the session id + workspace of an envelope that is about to ship.
 * Called from beforeSend AFTER the envelope passed every drop rule. A
 * missing session id or workspace never overwrites the record.
 */
export const recordAnalyzingSessionScope = (envelope) => {
  try {
    const sessionId = envelope?.session?.id
    const workspaceId = envelope?.workspace_id
    if (!sessionId || !workspaceId) return
    _sessionScopeStorage()?.setItem?.(
      ANALYZING_SESSION_SCOPE_KEY,
      JSON.stringify({ sessionId: String(sessionId), workspaceId: String(workspaceId) })
    )
  } catch (_) {
    /* best-effort */
  }
}

// Rotate when the tab's stored record says the CURRENT session id shipped
// under another workspace than `next`. Same order as the live switch: the
// old session ends while the stamp is still ITS workspace, then the new
// session opens with a session.start. Returns true when it rotated (and
// stamped `next`).
const _rotateIfStoredScopeDiffers = (next) => {
  if (!next || !_instance || typeof _instance.startNewSession !== 'function') return false
  const stored = _readSessionScope()
  if (!stored) return false
  let current = null
  try {
    current = _instance.sessionId
  } catch (_) {
    return false
  }
  if (!current || stored.sessionId !== String(current) || stored.workspaceId === next) return false
  _workspaceId = stored.workspaceId
  try {
    _instance.startNewSession()
  } catch (_) {
    /* the tracker stays on the old id; the stamp still moves on */
  }
  _workspaceId = next
  try {
    _instance.captureMessage('session.start', 'info', {
      reason: 'workspace-boot',
      workspaceId: next
    })
  } catch (_) {}
  _lastPageViewPath = null
  return true
}

// Test seam — lets the rotation rule be exercised without a browser boot.
export const __setAnalyzingInstanceForTests = (instance) => {
  _instance = instance || null
}

export const getAnalyzingWorkspace = () => _workspaceId

/**
 * ONE resolver for the analyzing tenant stamp — every writer (boot,
 * onWorkspaceChange, the orgSwitched handler) MUST use it, in this order:
 *   1. `sdk._context.activeWorkspaceId` — set by switchWorkspace/switchOrg
 *      BEFORE `sdk.orgSwitched` fires; the authoritative per-tab truth.
 *   2. the tab's viewed workspace (`root.workspace.id` / `root.activeWorkspaceId`).
 *   3. JWT `active_workspace_id` (lags async re-mints — a stale one from the
 *      PREVIOUS org has been observed after a clean switch).
 *   4. the org id — only when no workspace id is known (pre-workspace boot).
 * Why one resolver: the stamp is a per-session invariant on the server
 * (session row keyed {_id, workspace}); a stamp CHANGE ends the session and
 * starts a new one (setAnalyzingWorkspace). Three writers with three
 * different orders flapped org-id → stale-hq-workspace → new-org during one
 * switch and produced three sessions (measured, ANALYZING-SESSION-ROTATE-1).
 */
export const resolveAnalyzingScopeId = ({ sdk, root, claims } = {}) =>
  sdk?._context?.activeWorkspaceId ||
  root?.workspace?.id ||
  root?.activeWorkspaceId ||
  claims?.active_workspace_id ||
  root?.jwtClaims?.active_workspace_id ||
  root?.org?.id ||
  claims?.workspace_org_id ||
  root?.jwtClaims?.workspace_org_id ||
  null

// Attribution key this instance booted with — set once, inside
// bootAnalyzing, from the caller's `appKey` (or the 'workspace' default).
// Exposed read-only (no setter — unlike `_workspaceId`, this is a
// boot-time identity, not a value that changes mid-session) so tests and
// QA can confirm what a given caller actually stamped without reaching
// into the instance internals.
let _appKey = null

export const getAnalyzingAppKey = () => _appKey

// Pure — no DOM/SDK dependency, so it's directly unit-testable without
// booting the real createAnalyzing() SDK (which needs a browser
// environment: addEventListener, sendBeacon, … that a plain `node --test`
// / `bun test` process doesn't provide). Omitted/falsy → the 'workspace'
// default (byte-identical to the prior hardcoded literal); any other
// value is coerced to String so a caller can't hand createAnalyzing()
// (which throws on a non-string appKey downstream) something that isn't one.
export const resolveBootAppKey = (appKey) => (appKey ? String(appKey) : 'workspace')

const releaseTag = () => {
  if (typeof globalThis !== 'undefined' && globalThis.__BUILD_SHA__)
    return String(globalThis.__BUILD_SHA__)
  if (typeof process !== 'undefined' && process.env?.BUILD_SHA) return process.env.BUILD_SHA
  return null
}

/**
 * Boot the singleton. Idempotent — calling twice returns the first instance.
 * Requires a live SDK reference — the transport routes envelopes through
 * sdk.execute('workspaceProject.analyzed', 'ingest', …).
 *
 * @param {object}  [opts]
 * @param {object}  opts.sdk
 * @param {object}  [opts.capture]
 * @param {string}  [opts.workspaceId]
 * @param {string}  [opts.appKey] - Attribution key stamped as `app.key` on
 *   every envelope, read server-side by AnalyzedWriteService.writeEnvelope
 *   as `envelope.project_id || app?.key` into the stored `projectId` (the
 *   field the /analytics Projects selector narrows on). Defaults to
 *   'workspace' when omitted — every current caller of this singleton
 *   (see the file-header ATTRIBUTION note) is the unified shell itself, so
 *   the default IS the correct value today, not a placeholder. A future
 *   caller booting a genuinely separate app (its own bootShell() entry —
 *   canvas/marketplace/preview/analytics standalone, or a workspace/apps/*
 *   platform feature app) should pass its own manifest's canonical `key`
 *   (e.g. 'system--canvas') here instead of accepting the default.
 */
export const bootAnalyzing = ({ sdk, capture, workspaceId, appKey } = {}) => {
  if (_instance || _booting) return _instance
  if (typeof window === 'undefined') return null
  if (!sdk) return null
  // Stash a reference so the lazy resolver below can pick up a swap-in if
  // the workspace shell ever re-mints its SDK (rare but possible during
  // session refresh). Surfacing on window also gives QA a probe point.
  try {
    window.__symbolsSdk = sdk
  } catch (_) {}
  if (workspaceId) _workspaceId = String(workspaceId)
  _appKey = resolveBootAppKey(appKey)
  _booting = true
  // The `@symbo.ls/analyzing` import is resolved inside an ASYNC function on
  // purpose. In the published payload frank rewrites a bare npm import into
  // `await import(...)`, and it refuses to inject that await into a
  // synchronous caller chain — `unresolvable-npm-module-sync-guard`, the last
  // finding that blocked the workspace-shell publish
  // (WORKSPACE-SHELL-FRANK-GATE-BLOCKS-REPUBLISH-1). Booting one microtask
  // later is invisible to callers: every call site ignores the return value,
  // and `getAnalyzing()` already answered null while `_booting` was true.
  // `identifyFromSdk` awaits `_bootPromise`, so the identity stamp still
  // lands on the real instance.
  // The inner `await` is load-bearing, not style: frank only injects the
  // dynamic `await import('@symbo.ls/analyzing')` into `_bootAnalyzingNow`
  // when EVERY call site of it in the project awaits it
  // (toJSON.js `hasUnawaitedCallSiteInProject`). The retained promise lets
  // `identifyFromSdk` join an in-flight boot.
  _bootPromise = (async () => {
    try {
      return await _bootAnalyzingNow({ sdk, capture })
    } catch (err) {
      console.warn('[analyzing] boot failed:', err?.message || err)
      return null
    }
  })()
  return _instance
}

// The actual tracker construction — async so the npm import can be awaited.
const _bootAnalyzingNow = async ({ sdk, capture }) => {
  try {
    _instance = createAnalyzing({
      // Pass a LAZY sdk resolver: the workspace shell may call bootAnalyzing
      // before the SDK has fully hydrated its services / execute proxy. By
      // resolving fresh on every envelope flush we tolerate a brief boot
      // race without dropping events.
      sdk: () => (typeof window !== 'undefined' && window.__symbolsSdk) || sdk,
      appKey: _appKey,
      release: releaseTag(),
      env: currentChannel(),
      level: 'info',
      sampleRate: 1,
      // Default-on user interaction capture for the workspace shell. The
      // smbls/plugins/analyze browser listeners emit one `pointer` event
      // per click/dblclick/contextmenu with `target.key` + `target.path`
      // resolved by walking up the DOM looking for `data-key` attributes
      // (which DOMQL emits on every element with a key). Callers may
      // override individual flags (pass `capture: { pointer: false }` to
      // opt out). Errors/console/network/navigation/performance/viewport
      // stay on via the underlying `remote` preset.
      capture: { pointer: true, keyboard: false, forms: false, ...(capture || {}) },
      // Symbols-dogfood scrub: never ship raw auth payloads or tokens. The
      // built-in redact catches the obvious cases (`password`, `token`,
      // `apikey`, `secret`); these additions cover symbols-specific shapes.
      redact: [/symbols_access_token/i, /workspace_access_token/i, /jwtClaims/i],
      consoleSink: false,
      memorySink: true,
      // beforeSend does three things:
      //   1. Drops the envelope entirely when there's no workspace scope
      //      (pre-login pages, signed-out users, boot race before the
      //      shell resolves the active workspace). Without this guard the
      //      tracker still POSTs to /core/analyzed/ingest with no
      //      workspace_id — the server now accepts both authed + anon
      //      ingest, so it falls through to req.body.workspace_id which
      //      is also missing and returns 400. Telemetry is workspace-
      //      scoped data by design; pre-login activity can't be
      //      attributed to a tenant and is silently dropped.
      //   2. Stamps `workspace_id` from the module-level `_workspaceId`
      //      so the server's /core/analyzed/ingest can scope the write
      //      per tenant without depending on `req.user.activeOrganization`.
      //   3. Drops our own ingest traffic so analyzing never observes
      //      itself observing.
      //   4. Stamps the analytics read side's family + zone facts
      //      (WS-ANALYTICS-FAMILY-SPLIT-UTC-TOTALS-1): every shell session
      //      is the WORKSPACE family (`kind`), and `page.utcOffset`
      //      (minutes east of UTC) lets the session detail name the
      //      visitor's zone. Guarded — never clobbers a caller-set kind,
      //      never throws on a foreign envelope shape; works with the
      //      currently published SDK (plain envelope fields).
      beforeSend: (envelope) => {
        if (!envelope) return envelope
        if (!_workspaceId) return null
        envelope.workspace_id = _workspaceId
        try {
          if (!envelope.kind) envelope.kind = 'workspace'
          const page = envelope.page && typeof envelope.page === 'object' ? envelope.page : {}
          if (page.utcOffset == null) {
            envelope.page = { ...page, utcOffset: -new Date().getTimezoneOffset() }
          }
        } catch (_) {
          /* stamp is best-effort */
        }
        if (!Array.isArray(envelope.events)) {
          recordAnalyzingSessionScope(envelope)
          return envelope
        }
        envelope.events = envelope.events.filter((e) => {
          if (e.type !== 'network') return true
          const u = String(e.url || '')
          if (u.includes('/analyzed/')) return false
          return true
        })
        if (!envelope.events.length) return null
        // The envelope ships: remember which workspace this session id now
        // belongs to, for the next full page load of this tab.
        recordAnalyzingSessionScope(envelope)
        return envelope
      }
    })
    // Boot with a stamp already known (bootAnalyzing's `workspaceId`): the
    // tracker may have reused a session id this tab shipped under ANOTHER
    // workspace before a full-page navigation — rotate BEFORE the state
    // activates, so no envelope ships the old id under the new workspace
    // (ANALYZING-SESSION-BOOT-ROTATE-1). A later boot set (null → id) goes
    // through setAnalyzingWorkspace instead.
    if (_workspaceId) _rotateIfStoredScopeDiffers(_workspaceId)
    // The plugin state activates inside DOMQL's create() → plugin lifecycle.
    // For pre-shell errors caught by the SDK's pre-boot window listeners we
    // call activate(null) so they flush immediately.
    try {
      _instance.state.activate(null)
    } catch (_) {}
    // Debug exposure — surfaces the analyzing instance on window so QA can
    // probe captureError / state / sessionId from the console. Never use
    // for application logic.
    try {
      if (typeof window !== 'undefined') {
        window.__symbolsAnalyzing = _instance
      }
    } catch (_) {}
    // Route/slot page views — see the section above getAnalyzing.
    try {
      _wirePageViews()
    } catch (_) {}
  } catch (err) {
    console.warn('[analyzing] boot failed:', err?.message || err)
    _instance = null
    _appKey = null
  } finally {
    _booting = false
  }
  return _instance
}

export const getAnalyzing = () => _instance

// ── Shell page views (WS-SHELL-ANALYZING-NO-PAGE-VIEW-EVENTS-1) ────────────
// The server's page-view predicate matches `message: 'page_view'` OR
// `type: 'navigation'` (AnalyzedController _PAGE_VIEW_EXPR). The smbls
// analyze plugin's `navigation` capture never fires for the shell — routes
// move through PageSlot slot swaps and `el.router`, not through the hook it
// wires — so the workspace family sat at 0 page views while the public
// tracker's own `page_view` capture (sdk/packages/analyzing/src/boot.js)
// counted the projects family fine. The shell therefore captures its own
// `page_view`, the same shape the public tracker emits: the aggregations
// read `payload.data.path` (_PAGE_PATH_EXPR).
//
// No history patch here (Rule 64 — never mutate window/history from the
// shell): AppShell's `installAppShellSync` (workspace/packages/shared)
// already patches pushState/replaceState ONCE per window and dispatches
// `locationchange` for both, and PageSlot mirrors the primary pane's slot
// route into the `?page=` query through that same patched API. Riding
// `locationchange` + `popstate` (Back/Forward) with read-only listeners
// covers router navigations AND slot swaps with one pair of hooks.

let _lastPageViewPath = null
let _pageViewWired = false

// The clean route for a page view, from a full href. A trackable slot route
// mirrored into `?page=` (PageSlot SLOT_URL_PARAM) IS the page the user is
// looking at, so it wins over the pathname; its own query/hash are stripped.
// The leading-slash guard keeps foreign `?page=` values (pagination numbers)
// from shadowing the real route. Pure — exported for unit tests.
export const resolvePageViewPath = (href) => {
  if (typeof href !== 'string' || !href) return null
  let url
  try {
    // Base makes relative hrefs resolvable (tests, foreign shells).
    url = new URL(href, 'https://shell.invalid')
  } catch (_) {
    return null
  }
  const slot = url.searchParams.get('page')
  if (slot && slot.startsWith('/')) {
    const clean = slot.split('?')[0].split('#')[0]
    if (clean) return clean
  }
  return url.pathname || '/'
}

/**
 * Capture one `page_view` event for `path`, deduped for identical
 * CONSECUTIVE paths (a replaceState reaffirm of the same route is not a
 * second view; A → B → A is three). Returns true only when an event was
 * captured. A missing instance never poisons the dedupe — the same path
 * retries fine after boot completes.
 */
export const trackPageView = (path) => {
  if (typeof path !== 'string' || !path) return false
  if (path === _lastPageViewPath) return false
  if (!_instance || typeof _instance.captureMessage !== 'function') return false
  _lastPageViewPath = path
  try {
    _instance.captureMessage('page_view', 'info', { path })
  } catch (_) {
    /* telemetry must never break navigation */
  }
  return true
}

const _onPageViewNav = () => {
  if (typeof window === 'undefined') return
  try {
    trackPageView(resolvePageViewPath(window.location.href))
  } catch (_) {}
}

const _wirePageViews = () => {
  if (_pageViewWired) return
  if (typeof window === 'undefined') return
  _pageViewWired = true
  window.addEventListener('locationchange', _onPageViewNav)
  window.addEventListener('popstate', _onPageViewNav)
  // The boot itself is the first page view (same as the public tracker's
  // initial capture). beforeSend still gates on workspace scope at flush.
  _onPageViewNav()
}

// Test seam — dedupe state is module-level and bun runs every file of the
// package in one process.
export const __resetPageViewsForTests = () => {
  _lastPageViewPath = null
}

/**
 * Stamp the active user's identity onto the analyzing instance. Prefers the
 * caller-supplied `seedUser` — the auth tick already holds the full session
 * user with ZERO network — and only falls back to sdk.getMe() when no seed
 * is given (getMe's in-flight cache lives 50ms, so the boot-time identify
 * was re-fetching /auth/me ~1.7s mid-boot for traits it already had —
 * tickets/workspace.md boot ticket #3). The SDK response is shaped
 * `{ user: { … } }` so we unwrap before reading traits. Idempotent — safe
 * to call repeatedly (e.g. on subscribeAuthSession refresh).
 */
export const identifyFromSdk = async (sdk, seedUser = null) => {
  if (!_instance && _bootPromise) {
    try {
      await _bootPromise
    } catch (_) {}
  }
  if (!_instance) return
  // Identity is the user id (see identifyAnalyzing): a seed without one is
  // no identity, so fall through to getMe() for the full record.
  if (seedUser && (seedUser.id || seedUser._id)) {
    identifyAnalyzing({ user: seedUser })
    return
  }
  if (!sdk?.getMe) return
  try {
    const me = await sdk.getMe()
    const user = me?.user || me || null
    if (user && (user.id || user._id)) {
      identifyAnalyzing({ user })
    }
  } catch (err) {
    // The shell calls identifyFromSdk eagerly on mount, before the user
    // signs in (and on every subscribeAuthSession refresh). When the
    // SDK has no token, an expired/rotated token, or a token that the
    // server rejects on signature, getMe() throws an auth-class error.
    // None of these are actionable from telemetry — the user hasn't
    // logged in yet, or just needs to re-log. subscribeAuthSession
    // re-fires identifyFromSdk the moment a fresh session lands, so we
    // lose nothing by swallowing. Real failure modes (server 5xx,
    // malformed response, network) still warn.
    const msg = err?.message || String(err)
    const status = err?.status || err?.cause?.status
    const isAuthClass =
      status === 401 ||
      /no token provided/i.test(msg) ||
      /invalid token/i.test(msg) || // "Invalid token: invalid signature", "Invalid token: expired"
      /invalid signature/i.test(msg) ||
      /token expired/i.test(msg) ||
      /unauthorized/i.test(msg)
    if (isAuthClass) return
    // Browser-level network aborts — Safari throws `TypeError: Load failed`
    // and Chrome throws `Failed to fetch` when a fetch is cancelled by
    // navigation (route guard redirect, sign-in flow rerouting, page
    // unload mid-request). identifyFromSdk fires from boot + every
    // subscribeAuthSession tick, so it's the most likely fetch to get
    // aborted during early-route transitions. None of these are
    // actionable telemetry — the SDK will re-identify on the next
    // session tick once the page settles.
    // (WORKSPACE-ANALYZING-IDENTIFY-PRE-AUTH-NOISE).
    const isNetworkAbort =
      /load failed/i.test(msg) ||
      /failed to fetch/i.test(msg) ||
      /networkerror/i.test(msg) ||
      /the operation was aborted/i.test(msg)
    if (isNetworkAbort) return
    console.warn('[analyzing] identifyFromSdk failed:', msg)
  }
}

/**
 * Stamp the active user onto every subsequent envelope — the user id and the
 * plan ONLY (fly docs/FUNNEL.md §2 "Privacy", lane G2, 2026-10-24): never the
 * e-mail, the name or the username, and the id never falls back to the
 * e-mail (it used to: `user.id || … || user.email`, traits { email, name,
 * username, plan }). A user without an id leaves the session anonymous. The
 * names / e-mails an analytics view shows are read by the server from its
 * own user record, keyed by this id. (@symbo.ls/analyzing's identify()
 * enforces the same allowlist from the release that carries lane G2.)
 * The workspace tenant is server-stamped from the JWT — no need to ship
 * the org id from the client.
 */
export const identifyAnalyzing = ({ user } = {}) => {
  if (!_instance) return
  const userId = user ? user.id || user._id || null : null
  if (!userId) {
    _instance.identify(null)
    return
  }
  const plan = user.plan || user.subscription_plan || null
  _instance.identify({
    userId: String(userId),
    traits: plan ? { plan } : {}
  })
}

/**
 * Wire `sdk.orgSwitched` → re-identify so the user record refreshes after
 * an org switch. The server still stamps the new workspace_id from the
 * refreshed JWT, so events land in the right tenant automatically.
 */
export const reidentifyOnOrgSwitch = (sdk, getStateRoot) => {
  if (!sdk?.rootBus?.on || !_instance) return () => {}
  const handler = (payload) => {
    const root = typeof getStateRoot === 'function' ? getStateRoot() : null
    identifyAnalyzing({ user: root?.user })
    // Refresh tenant scope so subsequent envelopes carry the new
    // workspace_id. Payload from `sdk.orgSwitched` has the canonical
    // `newOrgId`; fall back to root.org?.id /
    // root.jwtClaims.workspace_org_id when the bus echoes without a
    // payload (older emit paths).
    // Same resolver as boot and onWorkspaceChange — never `newOrgId` first:
    // an org id here vs a workspace id there is a stamp CHANGE, i.e. a
    // spurious session rotation (ANALYZING-SESSION-ROTATE-1).
    const nextId =
      resolveAnalyzingScopeId({ sdk, root }) ||
      payload?.newOrgId ||
      root?.jwtClaims?.workspace_org_id ||
      null
    if (nextId) setAnalyzingWorkspace(nextId)
  }
  sdk.rootBus.on('sdk.orgSwitched', handler)
  return () => {
    try {
      sdk.rootBus.off('sdk.orgSwitched', handler)
    } catch (_) {}
  }
}
